/* eslint-disable no-prototype-builtins */
import React, { useEffect, useMemo, useState } from 'react';
import { FILTER_FIELDS, FILTER_OPTIONS, FILTER_RETAILERS } from '_constants/old/filters';
import { CheckedList } from 'modules/Modals/old/FilterModal/components/CheckboxGroup';
import { CollapsedTab } from 'modules/Modals/old/FilterModal/components/CollapsedTab';
import ProximityFilterSection from 'modules/Modals/old/FilterModal/components/ProximityFilterSection';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import RadioButtonsOrganizer from 'ui-kit/old/radioButton/radioButtonsOrganizer';
import { FormGroup } from 'modules/Modals/old/FilterModal/components/FormGroup';
import { dropEmptyFilterSections } from 'modules/Modals/old/FilterModal/utils/dropEmptyFilterSections';
import { PERMISSIONS } from '_constants';
import { OldFilterEntity, AuthEntity, RetailerEntity } from '_entities';
import { BaseSelect } from 'ui-kit';
import { useHiddenFilters } from 'hooks';
import * as Styled from './styles';

const { getProfilePermissions } = AuthEntity.selectors;
const { setShadowFilter } = OldFilterEntity.actions;
const { fetchAtccYears } = RetailerEntity.actions;
const { getAtccYears: getAtccYearOptions } = RetailerEntity.selectors;

const { hostname } = window.location;
const IsVaAccount = hostname?.split('.')[0] === 'va';

function RetailerCharacteristics({ isResidential, corpOptions, isAzAccount, isMdAccount }) {
  const {
    filter: { shadowFilters },
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [isAtccYearsLoading, setIsAtccYearsLoading] = useState(false);
  const atccYearOptions = useSelector(getAtccYearOptions);
  const currentYear = new Date().getFullYear().toString();
  const [synarYear, setSynarYear] = useState(currentYear);
  const userPermission = useSelector(getProfilePermissions);
  // P20M-1308 — hide the cigarettes/cigars/e-cig/smokeless + Alcohol rows of Products sold (the
  // general "Sells tobacco" question stays), Store type, Store ownership, and the WIC/SNAP/pharmacy/
  // internet rows of Other retailer details unless the user has the matching permission. AVC is gated
  // separately (AZ/FL).
  const {
    canViewProductsSoldTobacco,
    canViewProductsSoldAlcohol,
    canViewStoreType,
    canViewStoreOwnership,
    canViewOtherRetailerDetails,
    canViewAvc,
  } = useHiddenFilters();

  const parseAtccYears = (atccValue) => {
    if (!atccValue || ['t', 'f'].includes(atccValue)) return [];
    return atccValue.split(',').map((y) => ({ label: y, value: y }));
  };

  const defaultAtccYear = useMemo(() => [{ label: currentYear, value: currentYear }], [currentYear]);
  const [atccYearError, setAtccYearError] = useState(false);
  const [atccYears, setAtccYears] = useState(() => {
    const parsed = parseAtccYears(shadowFilters[FILTER_FIELDS.ATCC]);
    return parsed.length > 0 ? parsed : defaultAtccYear;
  });

  const atccFilterValue = shadowFilters[FILTER_FIELDS.ATCC];

  useEffect(() => {
    setAtccYears(parseAtccYears(atccFilterValue));
  }, [atccFilterValue]);

  useEffect(() => {
    (async function getAtccYearsData() {
      setIsAtccYearsLoading(true);
      try {
        await dispatch(fetchAtccYears());
      } catch (err) {
        // Non-fatal: ATCC years filter shows empty when fetch fails.
      } finally {
        setIsAtccYearsLoading(false);
      }
    })();
  }, [dispatch]);

  const haSynar = userPermission.includes(PERMISSIONS.SYNAR_RETAILER_FIELD);

  const productsSoldCollapsed = [
    // The general "Sells tobacco" question is NOT in the P20M-1308 hide list (only the
    // cigarettes/cigars/e-cig/smokeless rows are), so it stays visible.
    {
      id: 2,
      title: 'Tobacco',
      field: FILTER_FIELDS.TOBACCO,
      options: FILTER_OPTIONS.SELLS_TOBACCO_OPTIONS,
    },
    !isAzAccount &&
      canViewProductsSoldTobacco && {
        id: 3,
        title: 'Sells cigarettes',
        field: FILTER_FIELDS.SELL_CIGARETTES,
        options: FILTER_OPTIONS.SELLS_CIGARETTES_OPTIONS,
        isSubcategory: true,
      },
    !isAzAccount &&
      canViewProductsSoldTobacco && {
        id: 4,
        title: 'Sells cigars',
        field: FILTER_FIELDS.SELL_CIGARS,
        options: FILTER_OPTIONS.SELLS_CIGARS_OPTIONS,
        isSubcategory: true,
      },
    !isAzAccount &&
      canViewProductsSoldTobacco && {
        id: 5,
        title: 'Sells e-cigarettes',
        field: FILTER_FIELDS.SELL_ECIG,
        options: FILTER_OPTIONS.SELLS_ECIG_OPTIONS,
        isSubcategory: true,
      },
    !isAzAccount &&
      canViewProductsSoldTobacco && {
        id: 6,
        title: 'Sells smokeless tobacco products',
        field: FILTER_FIELDS.SELL_SMOKELESS,
        options: FILTER_OPTIONS.SELLS_SMOKELESS_OPTIONS,
        isSubcategory: true,
      },
    !isAzAccount &&
      canViewProductsSoldAlcohol && {
        id: 7,
        title: 'Alcohol',
        field: FILTER_FIELDS.ALCOHOL,
        options: FILTER_OPTIONS.SELLS_ALCOHOL_OPTIONS,
      },
  ].filter(Boolean);

  const otherRetailerDetailsCollapsed = [
    !isAzAccount &&
      canViewOtherRetailerDetails && {
        id: 16,
        field: FILTER_FIELDS.WIC,
        options: FILTER_OPTIONS.WIC_OPTIONS,
      },
    !isAzAccount &&
      canViewOtherRetailerDetails && {
        id: 17,
        field: FILTER_FIELDS.SNAP,
        options: FILTER_OPTIONS.SNAP_OPTIONS,
      },
    !isAzAccount &&
      canViewOtherRetailerDetails && {
        id: 18,
        field: FILTER_FIELDS.PHARMACY,
        options: FILTER_OPTIONS.HAS_PHARMACY_OPTIONS,
      },
    canViewAvc && {
      id: 19,
      field: FILTER_FIELDS.HAS_AVC_AGREEMENT,
      options: FILTER_OPTIONS.HAS_AVC_AGREEMENT_OPTIONS,
    },
    !isAzAccount &&
      canViewOtherRetailerDetails && {
        id: 20,
        field: FILTER_FIELDS.INTERNET_SALES_VISTIS,
        options: FILTER_OPTIONS.INTERNET_SALES_OPTIONS,
      },
    isResidential && {
      id: 21,
      field: FILTER_FIELDS.RESIDENTIAL_LOCATION,
      options: FILTER_OPTIONS.RESIDENTIAL_LOCATION_OPTIONS,
    },
    // P20M-1353 — MD only: retailers with / without a control number.
    isMdAccount && {
      id: 30,
      field: FILTER_FIELDS.HAS_CONTROL_NUMBER,
      options: FILTER_OPTIONS.HAS_CONTROL_NUMBER_OPTIONS,
    },
  ].filter(Boolean);

  const RETAILER_CHARACTERISTICS_TABS = [
    {
      type: 'multiple',
      title: 'Products sold',
      id: 1,
      collapsed: productsSoldCollapsed,
    },
    IsVaAccount && {
      type: 'multiple',
      title: 'Gambling',
      id: 8,
      collapsed: [
        {
          id: 9,
          title: 'Sells lottery tickets',
          field: FILTER_FIELDS.SELLS_TICKETS,
          options: FILTER_OPTIONS.SELLS_TICKETS_OPTIONS,
        },
        {
          id: 10,
          title: 'Has gaming machines',
          field: FILTER_FIELDS.HAS_GAMING_MACHINES,
          options: FILTER_OPTIONS.HAS_GAMING_MACHINES_OPTIONS,
        },
      ],
    },
    canViewStoreType && {
      id: 11,
      field: FILTER_FIELDS.RETAILER_BUSINESS_TYPE,
      title: 'Store type',
      options: FILTER_OPTIONS.STORE_TYPE_OPTIONS,
      type: 'single',
    },
    {
      type: 'proximity',
      title: 'Proximity',
      id: 12,
    },
    {
      type: 'double',
      title: 'Other retailer details',
      id: 15,
      collapsed: otherRetailerDetailsCollapsed,
    },
    // Gated purely on view_filter_store_ownership now — no AZ-subdomain carve-out, so an AZ account that
    // holds the permission (or the master override) sees the group like anyone else.
    canViewStoreOwnership && {
      type: 'double',
      title: 'Store ownership',
      id: 23,
      collapsed: [
        // {
        //   id: 24,
        //   field: FILTER_FIELDS.CHAIN,
        //   options: FILTER_OPTIONS.CHAIN_OPTIONS,
        // },
        // P20M-1308 follow-up — hidden by default everywhere; shown only with the
        // view_filter_store_ownership permission (or the master override). The permission
        // gates the row, not the section header, so dropEmptyFilterSections removes the
        // whole section when nothing inside is visible — including accounts that simply
        // have no corporation data. The AZ account is not special-cased: it follows the
        // same permission as every other account.
        canViewStoreOwnership &&
          corpOptions.length > 0 && {
            id: 25,
            field: FILTER_FIELDS.CORPORATION,
            options: corpOptions,
          },
      ].filter(Boolean),
    },
    IsVaAccount && {
      type: 'multiple',
      title: 'Hemp-Derived Cannabis Products',
      id: 26,
      collapsed: [
        {
          id: 27,
          title: 'Sales Status',
          field: FILTER_FIELDS.SELLS_HEMP,
          options: FILTER_OPTIONS.SELLS_HEMP_OPTIONS,
        },
        {
          id: 28,
          title: 'Product Types',
          field: FILTER_FIELDS.SELLS_HEMP_TYPES,
          options: FILTER_OPTIONS.SELLS_HEMP_TYPES_OPTIONS,
        },
        {
          id: 29,
          title: 'Outdoor Advertising',
          field: FILTER_FIELDS.HAS_OUTDOOR_AD_HEMP,
          options: FILTER_OPTIONS.HAS_OUTDOOR_AD_HEMP_OPTIONS,
        },
      ],
    },
  ];

  const filters = {
    [FILTER_FIELDS.ATCC]: null,
  };

  const handleAtccYearChange = (selectedOptions) => {
    const years = selectedOptions || [];
    setAtccYears(years);
    if (years.length > 0) setAtccYearError(false);
    const joinedValue = years.map((o) => o.value).join(',') || null;
    handleChangeFiltersYear(FILTER_FIELDS.ATCC, { value: joinedValue });
  };

  const handleInputChange = (e, fieldName) => {
    const inputValue = e.target.value;

    const numericValue = inputValue.replace(/\D/g, '').slice(0, 4);

    switch (fieldName) {
      case FILTER_FIELDS.SYNAR_YEAR:
        setSynarYear(numericValue);
        break;

      default:
        break;
    }

    if (numericValue.length === 4) {
      handleChangeFiltersYear(fieldName, { value: numericValue });
    }
  };

  const RADIO_BUTTONS = [
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_IS_ATCC,
      checked: shadowFilters[FILTER_FIELDS.ATCC] === 't',
    },
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_ATCC_CERTAIN_YEAR,
      checked: !!(shadowFilters[FILTER_FIELDS.ATCC] && !['t', 'f'].includes(shadowFilters[FILTER_FIELDS.ATCC])),
      customComponent: (
        <Styled.BasedSelectWrapper>
          <BaseSelect
            inputId="atcc-year-select"
            options={atccYearOptions}
            value={atccYears}
            onChange={handleAtccYearChange}
            isMulti
            isLoading={isAtccYearsLoading}
            menuPlacement="bottom"
            error={atccYearError ? 'Please select at least one year' : ''}
          />
        </Styled.BasedSelectWrapper>
      ),
    },
    {
      value: FILTER_RETAILERS.ATCC_OPTIONS.OPTION_NOT_ATCC,
      checked: shadowFilters[FILTER_FIELDS.ATCC] === 'f',
    },
  ];

  const SYNAR_RADIO = [
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_IS_SYNAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === 't',
    },
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_SYNAR_CERTAIN_YEAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === synarYear,
      customComponent: (
        <Styled.TextInputContainer>
          <Styled.TextInputLabel htmlFor="year-input" />
          <Styled.TextInput
            id="year-input"
            type="text"
            value={synarYear}
            onChange={(e) => handleInputChange(e, FILTER_FIELDS.SYNAR_YEAR)}
            placeholder={synarYear}
            maxLength={4}
          />
        </Styled.TextInputContainer>
      ),
    },
    {
      value: FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_NOT_SYNAR,
      checked: shadowFilters[FILTER_FIELDS.SYNAR_YEAR] === 'f',
    },
  ];

  const getElement = (item) => {
    switch (item.type) {
      case 'single':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <CheckedList name={item.field} options={item.options} />
          </CollapsedTab>
        );
      case 'multiple':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <Styled.FiltersContainer>
                <Styled.FiltersTitle isSubcategory={elem?.isSubcategory}>{elem.title}</Styled.FiltersTitle>
                <CheckedList name={elem.field} options={elem.options} isSubcategory={elem?.isSubcategory} />
              </Styled.FiltersContainer>
            ))}
          </CollapsedTab>
        );
      case 'double':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <CheckedList name={elem.field} options={elem.options} />
            ))}
          </CollapsedTab>
        );
      case 'proximity':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <ProximityFilterSection />
          </CollapsedTab>
        );
      default:
        return null;
    }
  };

  const handleChangeFiltersYear = (field, { value }) => {
    const newFilters = { ...shadowFilters };
    switch (field) {
      case FILTER_FIELDS.ATCC:
      case FILTER_FIELDS.SYNAR_YEAR:
        dispatch(
          setShadowFilter({
            ...newFilters,
            ...{ [field]: value },
          }),
        );
        break;
      default:
        break;
    }
  };

  const handleRadioButton = (value, fieldName, year) => {
    switch (value) {
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_IS_ATCC:
        setAtccYearError(false);
        filters[fieldName] = 't';
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_IS_SYNAR:
        filters[fieldName] = 't';
        break;
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_ATCC_CERTAIN_YEAR:
        if (!year) {
          setAtccYearError(true);
          return;
        }
        setAtccYearError(false);
        filters[fieldName] = year;
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_SYNAR_CERTAIN_YEAR:
        filters[fieldName] = year;
        break;
      case FILTER_RETAILERS.ATCC_OPTIONS.OPTION_NOT_ATCC:
        setAtccYearError(false);
        filters[fieldName] = 'f';
        break;
      case FILTER_RETAILERS.SYNAR_OPTIONS.OPTION_NOT_SYNAR:
        filters[fieldName] = 'f';
        break;
      default:
        break;
    }
    dispatch(setShadowFilter({ ...shadowFilters, ...filters }));
  };

  return (
    <Styled.Wrapper>
      {dropEmptyFilterSections(RETAILER_CHARACTERISTICS_TABS).map((item) => getElement(item))}{' '}
      {isMdAccount && (
        <FormGroup title="ATCC">
          <RadioButtonsOrganizer
            hideResetButton
            radioButtons={RADIO_BUTTONS}
            onChange={({ value }) =>
              handleRadioButton(value, FILTER_FIELDS.ATCC, atccYears.map((o) => o.value).join(',') || null)
            }
            name="atcc"
            isColumn
          />
        </FormGroup>
      )}
      {haSynar && (
        <FormGroup title="SYNAR">
          <RadioButtonsOrganizer
            hideResetButton
            radioButtons={SYNAR_RADIO}
            onChange={({ value }) => handleRadioButton(value, FILTER_FIELDS.SYNAR_YEAR, synarYear)}
            name="synar_year"
            isColumn
          />
        </FormGroup>
      )}
    </Styled.Wrapper>
  );
}

RetailerCharacteristics.propTypes = {
  // isVaAccount: PropTypes.bool,
  isMdAccount: PropTypes.bool,
  isAzAccount: PropTypes.bool,
  isResidential: PropTypes.bool,
  corpOptions: PropTypes.array,
};

RetailerCharacteristics.defaultProps = {
  // isVaAccount: false,
  isMdAccount: false,
  isAzAccount: false,
  isResidential: false,
  corpOptions: [],
};

export default RetailerCharacteristics;
